/**
 * @module services/patient-portal
 * @description UC-REG-304 (Campana, Diego 2026-10-29) — Perfil del portal del
 *              paciente por tenant, resuelto POR CÓDIGO a partir del theme del
 *              build (`VUE_APP_THEME`, que `vue.config.js` toma de `THEME`; el
 *              workflow `deploy-campana.yml` lo fija en "campana"). Sin
 *              variables de entorno nuevas, a pedido de Diego.
 *
 *              Campana = portal "solo prescripciones":
 *              - sin "Mi credencial" en el menú (sidebar y barra inferior; el
 *                banner del Home sigue abriendo la credencial);
 *              - "Mi historia clínica" pasa a llamarse "Mis prescripciones" y
 *                muestra solo lo que el médico prescribió: medicamentos,
 *                laboratorios, imágenes y certificados — sin evoluciones,
 *                notas, formularios, prácticas, programas ni resultados, y sin
 *                carga de resultados por el paciente;
 *              - sin la tarjeta "Tareas de salud" del Home.
 *              - UC-REG-306: "Gestionar cita" (Turnos) lleva a "Mis trámites":
 *                el vecino no saca turnos, se le asignan sobre un trámite.
 *              - UC-REG-307: "Mis prescripciones" sin el acordeón "Atenciones
 *                previas": directamente las secciones por tipo.
 *
 *              Cualquier otro theme conserva el comportamiento histórico.
 *              La misma vista `/patient/me` la embebe la app móvil en un
 *              WebView, así que el recorte aplica también allí.
 */
import { theme } from "@/services/config";
import { INDICATIONS_TYPES } from "@/services/constants";

/** @type {string[]} Themes (tenants) con portal "solo prescripciones". */
export const PRESCRIPTIONS_ONLY_THEMES = ["campana"];

/** @type {string[]} Tipos de indicación que ve el paciente en el portal solo-prescripciones. */
export const PATIENT_PRESCRIPTION_TYPES = [
  "medications",
  "lab",
  "images",
  "certificates",
];

/**
 * @type {string[]} Tipos ocultos al paciente en la historia clínica histórica
 *                  (lista original de `PatientHistory.vue`, sin cambios).
 */
export const DEFAULT_PATIENT_HIDDEN_TYPES = [
  "evolution",
  "forms",
  "practices",
  "programs",
];

/**
 * @description Tipos que NO están en el catálogo `INDICATIONS_TYPES` pero
 *              pueden aparecer en una atención y deben ocultarse igual en el
 *              portal solo-prescripciones.
 * @type {string[]}
 */
const EXTRA_HIDDEN_TYPES = [
  ...DEFAULT_PATIENT_HIDDEN_TYPES,
  "labResults",
  "imagesResults",
];

/**
 * @description Arma el perfil del portal del paciente para un theme.
 * @param {string|undefined} themeName - theme del build (`VUE_APP_THEME`)
 * @returns {{
 *   prescriptionsOnly: boolean,
 *   medicalRecordLabel: string,
 *   medicalRecordNavLabel: string,
 *   medicalRecordCard: {title: string, subtitle: string},
 *   showCredentialMenuItem: boolean,
 *   showHealthTasksCard: boolean,
 *   canUploadStudyResults: boolean,
 *   hiddenIndicationTypes: string[],
 *   manageAppointmentRoute: ({name: string}|null),
 *   showPreviousAttentions: boolean
 * }}
 */
export function buildPatientPortal(themeName) {
  const prescriptionsOnly = PRESCRIPTIONS_ONLY_THEMES.includes(themeName);

  let hiddenIndicationTypes = DEFAULT_PATIENT_HIDDEN_TYPES;
  if (prescriptionsOnly) {
    const catalogTypes = INDICATIONS_TYPES.map((t) => t.type).filter(Boolean);
    hiddenIndicationTypes = Array.from(
      new Set([...catalogTypes, ...EXTRA_HIDDEN_TYPES])
    ).filter((type) => !PATIENT_PRESCRIPTION_TYPES.includes(type));
  }

  return {
    prescriptionsOnly,
    medicalRecordLabel: prescriptionsOnly
      ? "Mis prescripciones"
      : "Mi historia clínica",
    medicalRecordNavLabel: prescriptionsOnly ? "Prescripciones" : "Historia",
    medicalRecordCard: prescriptionsOnly
      ? {
          title: "Mis prescripciones",
          subtitle: "Recetas, estudios y certificados",
        }
      : { title: "Ver órdenes médicas", subtitle: "Tus indicaciones" },
    showCredentialMenuItem: !prescriptionsOnly,
    showHealthTasksCard: !prescriptionsOnly,
    canUploadStudyResults: !prescriptionsOnly,
    hiddenIndicationTypes,
    // UC-REG-306: en Campana los turnos se asignan siempre sobre un trámite.
    manageAppointmentRoute: prescriptionsOnly
      ? { name: "my-procedures" }
      : null,
    // UC-REG-307: el paciente ve directamente las secciones por tipo.
    showPreviousAttentions: !prescriptionsOnly,
  };
}

/** @type {ReturnType<typeof buildPatientPortal>} Perfil del build actual. */
export const patientPortal = buildPatientPortal(theme);

/**
 * @description Si el paciente ve un tipo de indicación en su portal.
 * @param {string} type - tipo de indicación (`medications`, `lab`, ...)
 * @returns {boolean}
 */
export function isPatientIndicationTypeVisible(type) {
  return !patientPortal.hiddenIndicationTypes.includes(type);
}

/**
 * @description UC-REG-306 — Qué hace "Gestionar cita" en Turnos del paciente:
 *              navegar a otra ruta (Campana: "Mis trámites") o abrir la
 *              pestaña del buscador de turnos (resto de los tenants).
 * @param {ReturnType<typeof buildPatientPortal>} [portal=patientPortal] - perfil
 * @returns {{route: {name: string}}|{tab: string}} ruta a la que navegar, o
 *          pestaña de Turnos a abrir
 */
export function resolveManageAppointment(portal = patientPortal) {
  if (portal.manageAppointmentRoute) {
    return { route: { ...portal.manageAppointmentRoute } };
  }
  return { tab: "sacar" };
}

export default patientPortal;
